// src/pages/seasons/ScheduleGateway.jsx
// Sidebar "Schedule" entry point. Finds the current team's most recent active
// season and redirects straight to its schedule page — zero extra clicks.
// Falls back to any season if none are open/closed, and to a helpful empty
// state if the team has no seasons at all.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, orderBy, limit, getDocs,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import { useRole } from '@/context/RoleContext';
import { CalendarDaysIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

// Lime/pitch design system — keep in sync with AcceptInvite,
// /admin pages, etc. Headings use Barlow Condensed uppercase; primary
// CTAs use the lime fill on pitch text.
const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function ScheduleGateway() {
  const { currentTeam } = useTeam();
  const { firebaseUser } = useAuth();
  const { hasCoachTeams, parentTeamIds } = useRole();
  const navigate        = useNavigate();
  const { t }           = useTranslation();
  const [noSeasons, setNoSeasons] = useState(false);

  useEffect(() => {
    let cancelled = false;

    /**
     * Resolve which team's schedule to show.
     *
     * Coaches: use currentTeam from TeamContext (the active coach
     *   team set by the sidebar picker).
     * Parents (and pure-parent users): look at parentTeamIds from
     *   RoleContext. If exactly one parent team, use it directly.
     *   If multiple, route to /dashboard so they pick a team. If
     *   zero coach AND zero parent teams, show the empty state.
     */
    (async () => {
      let teamId = currentTeam?.id || null;

      if (!teamId && firebaseUser?.uid && parentTeamIds.size > 0) {
        if (parentTeamIds.size === 1) {
          teamId = Array.from(parentTeamIds)[0];
        } else {
          // Multiple kids on multiple teams — bounce to the team
          // picker so they choose explicitly.
          navigate('/dashboard', { replace: true });
          return;
        }
      }

      if (!teamId) {
        // No team selected and no parent teams either. If the user
        // has coach teams, drop them on /seasons (the seasons list)
        // so they can create one. Otherwise show the friendly empty
        // state — they have nothing to look at yet.
        if (hasCoachTeams) {
          navigate('/seasons', { replace: true });
        } else {
          setNoSeasons(true);
        }
        return;
      }

      // 1st preference: open or closed season (active)
      try {
        const active = await getDocs(query(
          collection(db, 'seasons'),
          where('teamId', '==', teamId),
          where('status', 'in', ['open', 'closed']),
          orderBy('createdAt', 'desc'),
          limit(1),
        ));
        if (cancelled) return;
        if (!active.empty) {
          navigate(`/seasons/${active.docs[0].id}/schedule`, { replace: true });
          return;
        }

        // 2nd preference: any season (archived is fine — coaches still
        // view old schedules; parents see the empty state if no active.)
        const any = await getDocs(query(
          collection(db, 'seasons'),
          where('teamId', '==', teamId),
          orderBy('createdAt', 'desc'),
          limit(1),
        ));
        if (cancelled) return;
        if (!any.empty) {
          navigate(`/seasons/${any.docs[0].id}/schedule`, { replace: true });
        } else {
          setNoSeasons(true);
        }
      } catch (err) {
        if (import.meta.env.DEV) console.warn('[ScheduleGateway] season lookup failed', err);
        setNoSeasons(true);
      }
    })();

    return () => { cancelled = true; };
    // parentTeamIds is a Set — its identity changes when membership
    // changes. hasCoachTeams shifts only when the user gains/loses
    // a coach team. Both are stable inputs to the routing decision.
  }, [currentTeam?.id, firebaseUser?.uid, hasCoachTeams, parentTeamIds, navigate]);

  if (noSeasons) {
    // Empty-state copy adapts: a user with no coach teams (pure
    // parent) gets the friendly "your coach hasn't published yet"
    // wording; coaches see the create-a-season prompt below.
    if (!hasCoachTeams) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
            <CalendarDaysIcon className="h-7 w-7 text-[#0d1f14]/60" />
          </div>
          <div>
            <p
              className="uppercase text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22, letterSpacing: '0.4px' }}
            >
              No schedule yet
            </p>
            <p className="text-sm text-zinc-500 mt-1.5 max-w-sm">
              Your child's coach hasn't published a season schedule yet. You'll see games and practices here once they do.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-5 text-center px-6">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
          <CalendarDaysIcon className="h-7 w-7 text-[#0d1f14]/60" />
        </div>
        <div>
          <p
            className="uppercase text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 22, letterSpacing: '0.4px' }}
          >
            No seasons yet
          </p>
          <p className="text-sm text-zinc-500 mt-1.5 max-w-sm">{t('nav.noSeasonForSchedule')}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/seasons/create')}
          className="inline-flex items-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[48px] uppercase transition-colors"
          style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 15, letterSpacing: '0.6px' }}
        >
          Create a season
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Spinner while the redirect query runs — lime ring on a faint pitch
  // track so it reads in both light and dark contexts.
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="h-9 w-9 rounded-full border-4 border-[#0d1f14]/10 border-t-[#b5f03d] animate-spin" />
    </div>
  );
}
